import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Search, UserPlus, ArrowRight, Copy, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

function genPassword() {
  const words = ["Truck", "Fleet", "Road", "Cargo", "Haul", "Diesel", "Route", "Miles", "Axle", "Ranger"];
  const w = words[Math.floor(Math.random() * words.length)];
  const num = Math.floor(1000 + Math.random() * 9000);
  const sym = ["!", "@", "#", "$", "&"][Math.floor(Math.random() * 5)];
  return `${w}${num}${sym}`;
}

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/admin/clients${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="admin-clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="hs-overline text-blue-700">ROSTER</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">Clients</h1>
        </div>
        <button onClick={() => setShowCreate(true)} className="hs-btn-primary inline-flex items-center gap-2" data-testid="admin-create-client-button">
          <UserPlus size={14} /> ADD CLIENT
        </button>
      </div>

      <div className="hs-card p-3 flex gap-2">
        <Search size={16} className="text-slate-500 m-2" />
        <input
          className="hs-input !border-none !bg-transparent !p-2"
          placeholder="Search by name, email, company, DOT…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="admin-clients-search"
        />
        <button onClick={load} className="hs-btn-secondary">SEARCH</button>
      </div>

      <div className="hs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <Th>Name</Th><Th>Company</Th><Th>DOT / MC</Th><Th>Pending Docs</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 hs-overline">LOADING…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="p-12 text-center text-sm text-slate-500">No clients found.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`client-row-${c.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.email}</div>
                </td>
                <td className="px-4 py-3">{c.company_name || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">DOT {c.dot_number || "—"}<br/>MC {c.mc_number || "—"}</td>
                <td className="px-4 py-3 font-mono">{c.pending_documents || 0}</td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/admin/clients/${c.id}`} className="hs-btn-ghost inline-flex items-center gap-1 text-xs" data-testid={`client-open-${c.id}`}>
                    OPEN <ArrowRight size={12} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateClientModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await load(); toast.success("Client created"); }}
        />
      )}
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 hs-overline">{children}</th>; }

function CreateClientModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", password: genPassword(), name: "", company_name: "", phone: "", dot_number: "", mc_number: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/clients", form);
      setCreated({ email: form.email, password: form.password, name: form.name, company: form.company_name, id: data?.id });
      toast.success("Client created — copy credentials below");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const copyCreds = async () => {
    if (!created) return;
    const text = `Portal: ${window.location.origin}\nEmail: ${created.email}\nPassword: ${created.password}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Credentials copied to clipboard");
    } catch {
      toast.error("Copy failed — select manually");
    }
  };

  // Post-creation handoff screen
  if (created) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
        <div className="hs-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="create-client-handoff">
          <div className="hs-overline text-emerald-700">CLIENT CREATED ✓</div>
          <h3 className="font-display font-bold text-2xl mt-1">Hand these credentials to the client</h3>
          <div className="mt-5 bg-emerald-50 border-2 border-emerald-300 p-5">
            <div className="hs-overline text-emerald-800 mb-3">CLIENT CREDENTIALS · SHARE THESE</div>
            <div className="space-y-4">
              <div>
                <div className="hs-overline text-[10px] text-slate-500 mb-1">EMAIL</div>
                <div className="font-mono text-base font-bold text-slate-900 break-all" data-testid="handoff-email">{created.email}</div>
              </div>
              <div>
                <div className="hs-overline text-[10px] text-slate-500 mb-1">PASSWORD</div>
                <div className="font-mono text-2xl font-black text-emerald-900 tracking-wide select-all bg-white border border-emerald-400 px-3 py-2" data-testid="handoff-password">{created.password}</div>
              </div>
              <div>
                <div className="hs-overline text-[10px] text-slate-500 mb-1">PORTAL URL</div>
                <div className="font-mono text-sm text-slate-700 break-all">{window.location.origin}</div>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3">Save or copy now — for security this password is only shown once here. The client cannot change or reset it themselves; only your team can reset it later via the client detail page.</p>
          <div className="mt-6 flex justify-between gap-2">
            <button type="button" onClick={copyCreds} className="hs-btn-secondary inline-flex items-center gap-2" data-testid="handoff-copy-btn">
              <Copy size={14}/> COPY ALL
            </button>
            <button type="button" onClick={() => { onCreated(); }} className="hs-btn-primary" data-testid="handoff-done-btn">DONE</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="hs-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="create-client-modal">
        <div className="hs-overline">ADD</div>
        <h3 className="font-display font-bold text-2xl">New Client</h3>
        <p className="text-xs text-slate-500 mt-1">You set the password here and hand it to the client. They cannot change it.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <Field label="Name" required value={form.name} onChange={set("name")} testid="create-client-name" />
          <Field label="Email (login)" type="email" required value={form.email} onChange={set("email")} testid="create-client-email" />
          <div className="sm:col-span-2">
            <label className="hs-overline flex items-center justify-between">
              <span>Password (client's login password)</span>
              <button type="button" onClick={() => setForm({ ...form, password: genPassword() })} className="hs-btn-ghost inline-flex items-center gap-1 text-[10px]" data-testid="create-generate-pwd">
                <RefreshCcw size={10}/> GENERATE
              </button>
            </label>
            <input
              type="text"
              required
              minLength={8}
              value={form.password}
              onChange={set("password")}
              className="hs-input mt-2 font-mono text-base"
              data-testid="create-client-password"
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              name="new_client_login_password"
              spellCheck={false}
            />
            <div className="mt-2 bg-blue-50 border border-blue-300 px-3 py-2 flex items-center gap-2" data-testid="create-password-preview">
              <span className="hs-overline text-[10px] text-blue-700">PASSWORD YOU&rsquo;LL SEND →</span>
              <span className="font-mono text-lg font-bold text-blue-900 select-all break-all flex-1">{form.password || "—"}</span>
            </div>
          </div>
          <Field label="Phone" value={form.phone} onChange={set("phone")} testid="create-client-phone" />
          <Field label="Company" value={form.company_name} onChange={set("company_name")} testid="create-client-company" />
          <Field label="DOT #" value={form.dot_number} onChange={set("dot_number")} testid="create-client-dot" />
          <Field label="MC #" value={form.mc_number} onChange={set("mc_number")} testid="create-client-mc" />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="hs-btn-secondary">CANCEL</button>
          <button type="submit" disabled={busy} className="hs-btn-primary" data-testid="create-client-submit">{busy ? "CREATING…" : "CREATE"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="hs-overline">{label}</label>
      <input className="hs-input mt-2" data-testid={testid} {...rest} />
    </div>
  );
}
